import React, { useState } from "react"
import {
  Flex, Stack, Box, Image, Heading, Text,
} from "@chakra-ui/react"
import { useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import * as Yup from "yup"
import Button from "components/Buttons/Button"
import Spinner from "components/elements/Spinner"
import { redeemLeadRetrievalLicense } from "api/events"
import { LeadRetrievalLicenseTokenResponse, PublicDioboxEvent } from "sharedTypes"
import SegmentTitle from "../SegmentTitle"
import PersonalDataScreen from "../Steps/PersonalDataScreen"
import AlreadyHaveLicenseScreen from "../Steps/AlreadyHaveLicenseScreen"
import PaymentSuccessScreen from "../Steps/PaymentSuccessScreen"
import InvalidLinkScreen from "./InvalidLinkScreen"
import ConfirmAccountScreen from "./ConfirmAccountScreen"
import WrongAccountScreen from "./WrongAccountScreen"

type Props = {
  event: PublicDioboxEvent
  token: string
  tokenResponse?: LeadRetrievalLicenseTokenResponse
  isTokenLoading: boolean
  isTokenError: boolean
}

const LeadRetrievalInviteRedeem = ({
  event, token, tokenResponse, isTokenLoading, isTokenError,
}: Props) => {
  const [notMe, setNotMe] = useState(false)
  const [redeemed, setRedeemed] = useState(false)

  const hasExistingUser = !!tokenResponse?.user

  const schema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    password: hasExistingUser
      ? Yup.string().nullable()
      : Yup.string().min(12, "Password must be at least 12 characters").required("Password is required"),
  })

  const {
    control, handleSubmit, formState: { isValid, isSubmitting },
  } = useForm({
    defaultValues: { name: "", password: "" },
    resolver: yupResolver(schema),
  })

  const {
    eventImageUrl, name, dateTimeLocation, showEventImage, eventImageScale,
  } = event
  const hasEventImage = showEventImage && eventImageUrl
  const eventImageScaleWidth = 1332

  const onSubmit = handleSubmit(async (values) => {
    await redeemLeadRetrievalLicense(token, {
      name: values.name,
      password: values.password || undefined,
    })
    setRedeemed(true)
  })

  const email = tokenResponse?.user?.email || tokenResponse?.email || ""

  const screen = (() => {
    if (isTokenLoading) return "loading" as const
    if (isTokenError || !tokenResponse) return "invalid" as const
    if (redeemed) return "success" as const
    if (tokenResponse.alreadyRedeemed) return "alreadyRedeemed" as const
    if (notMe) return "wrongAccount" as const
    if (hasExistingUser) return "confirmAccount" as const

    return "personalData" as const
  })()

  const renderBody = () => {
    switch (screen) {
      case "loading":
        return <Spinner />
      case "invalid":
        return <InvalidLinkScreen />
      case "success":
        return <PaymentSuccessScreen email={email} />
      case "alreadyRedeemed":
        return (
          <AlreadyHaveLicenseScreen
            leadRetrievalUser={{ email }}
            description={(
              <Text>
                This license has already been activated. Download the app and log in to get
                started.
              </Text>
            )}
          />
        )
      case "wrongAccount":
        return <WrongAccountScreen />
      case "confirmAccount":
        return (
          <ConfirmAccountScreen
            control={control}
            email={email}
            onNotMe={() => setNotMe(true)}
          />
        )
      default:
        return (
          <PersonalDataScreen
            control={control}
            emailAddress={email}
            description="Complete your account setup with this email address by entering your
              exhibitor name and creating a password. Your license will be activated immediately
              — no payment required."
          />
        )
    }
  }

  const showSubmitButton = screen === "confirmAccount" || screen === "personalData"

  return (
    <Flex direction="column" bg="gray.50" h="100dvh" overflowY="scroll">
      <Flex my={12} flexDirection="column" alignItems="center" gap={4}>
        <Stack spacing={6} borderRadius="lg" bg="white" borderWidth={2} maxW={670} p={8} mx={4} overflow="hidden">
          <Flex flexDirection="column" alignItems="center" mx={-8} mt={-8}>
            {hasEventImage && (
            <Image
              src={`${eventImageUrl}?fit=crop&w=${eventImageScaleWidth * (eventImageScale / 100)}`}
              w={`${eventImageScale}%`}
              borderTopLeftRadius="lg"
              borderTopRightRadius="lg"
              alt={name}
            />
            )}
            <Heading mt={4} size="lg">{name}</Heading>
            <Box
              textAlign="center"
              dangerouslySetInnerHTML={{ __html: dateTimeLocation }}
              mb={3}
              fontSize="lg"
            />
          </Flex>
          <SegmentTitle leftText="Lead Retrieval App" />
          {renderBody()}
        </Stack>
        {showSubmitButton && (
          <Button isDisabled={!isValid} onClick={onSubmit} isLoading={isSubmitting}>
            Activate License
          </Button>
        )}
      </Flex>
    </Flex>
  )
}

export default LeadRetrievalInviteRedeem
